import { useApi } from './apiService';
import { toBase64 } from '../../common/Utils/base64';

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
  estimatedTimeRemaining?: number;
  uploadSpeed?: number;
}

export interface SoftwareDeploymentData {
  file: File;
  appName: string;
  publisher: string;
  osType: string;
  detection: string;
  installationParameters?: string;
  preScript?: string;
  installScript?: string;
  postScript?: string;
}

export interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  onUploadStrategy?: (strategy: 'single' | 'block-based', fileSize: number) => void;
  timeout?: number;
  retryAttempts?: number;
}

export const useSoftwareDeployment = () => {
  const api = useApi();

  // Constants matching backend configuration
  const MAX_FILE_SIZE = 1_000_000_000; // 1GB
  const LARGE_FILE_THRESHOLD = 100 * 1024 * 1024; // 100MB
  const DEFAULT_TIMEOUT = 30 * 60 * 1000; // 30 minutes

  const validateFile = (file: File): { isValid: boolean; error?: string } => {
    if (!file) {
      return { isValid: false, error: 'No file selected' };
    }

    if (file.size === 0) {
      return { isValid: false, error: 'File is empty' };
    }

    if (file.size > MAX_FILE_SIZE) {
      const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
      const maxSizeMB = (MAX_FILE_SIZE / 1024 / 1024).toFixed(0);
      return {
        isValid: false,
        error: `File size (${fileSizeMB} MB) exceeds maximum allowed size (${maxSizeMB} MB)`,
      };
    }

    return { isValid: true };
  };

  const createFormData = (data: SoftwareDeploymentData): FormData => {
    const formData = new FormData();

    // Note: Fixed field name mapping to match backend expectations
    formData.append('file', data.file);
    formData.append('ApplicationName', data.appName);
    formData.append('Publisher', data.publisher);
    formData.append('OperatingSystemType', data.osType);
    formData.append('DetectionMechanism', data.detection);
    formData.append('OperatingSystemVersion', '11');

    // Convert installation parameters to base64
    const installationParamsBase64 = data.installationParameters ? toBase64(data.installationParameters) : '';
    formData.append('InstallationParameters', installationParamsBase64);

    formData.append('PreInstallScript', data.preScript || '');
    formData.append('InstallScript', data.installScript || '');
    formData.append('PostInstallScript', data.postScript || '');

    return formData;
  };

  const calculateUploadMetrics = (loaded: number, total: number, startTime: number): UploadProgress => {
    const percentage = Math.round((loaded / total) * 100);
    const elapsedTime = (Date.now() - startTime) / 1000; // seconds
    const uploadSpeed = loaded / elapsedTime; // bytes per second
    const remainingBytes = total - loaded;
    const estimatedTimeRemaining = uploadSpeed > 0 ? remainingBytes / uploadSpeed : undefined;

    return {
      loaded,
      total,
      percentage,
      estimatedTimeRemaining,
      uploadSpeed,
    };
  };

  const addSoftwareDeployment = async (tenantId: string, token: string, data: SoftwareDeploymentData, options: UploadOptions = {}) => {
    // Validate file before attempting upload
    const validation = validateFile(data.file);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    // Determine upload strategy and notify user
    const isLargeFile = data.file.size > LARGE_FILE_THRESHOLD;
    const uploadStrategy = isLargeFile ? 'block-based' : 'single';

    if (options.onUploadStrategy) {
      options.onUploadStrategy(uploadStrategy, data.file.size);
    }

    // Log upload details for debugging
    // console.log(`Starting upload: ${data.file.name} (${(data.file.size / 1024 / 1024).toFixed(2)} MB) using ${uploadStrategy} strategy`);

    const formData = createFormData(data);
    const startTime = Date.now();
    const timeout = options.timeout || DEFAULT_TIMEOUT;
    const maxRetries = options.retryAttempts || (isLargeFile ? 3 : 1);

    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // console.log(`Upload attempt ${attempt}/${maxRetries} for ${data.file.name}`);

        const response = await api.post(`softwaredeploymentservice/SoftwareDeployment/add-software/${tenantId}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout,
          onUploadProgress: (progressEvent?: { loaded?: number; total?: number }) => {
            if (!options.onProgress) {
              return;
            }

            const loaded = progressEvent?.loaded ?? 0;
            const total = progressEvent?.total ?? data.file.size;

            if (total > 0) {
              const progress = calculateUploadMetrics(loaded, total, startTime);
              options.onProgress(progress);
            }
          },
          // Additional configurations for large files
          maxContentLength: MAX_FILE_SIZE,
          maxBodyLength: MAX_FILE_SIZE,
        });

        // console.log(`Upload completed successfully for ${data.file.name} in ${((Date.now() - startTime) / 1000).toFixed(2)}s`);
        return response.data;
      } catch (error: any) {
        lastError = error;

        // Log detailed error information
        const errorDetails = {
          attempt,
          fileName: data.file.name,
          fileSize: data.file.size,
          uploadStrategy,
          error: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
        };

        console.error('Upload attempt failed:', errorDetails);

        // Don't retry on certain errors
        if (error.response?.status === 400 || error.response?.status === 413) {
          console.error('Non-retryable error, aborting upload');
        }

        // Wait before retry (exponential backoff)
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000); // Max 10 seconds
          // console.log(`Retrying upload in ${delay}ms...`);
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }

    // All retries failed
    console.error(`Upload failed after ${maxRetries} attempts for ${data.file.name}`);

    // Provide user-friendly error messages
    if (lastError.response?.status === 413) {
      throw new Error(`File is too large. Maximum size allowed is ${MAX_FILE_SIZE / 1024 / 1024} MB.`);
    } else if (lastError.response?.status === 400) {
      throw new Error(`Invalid file or request. Please check the file and try again.`);
    } else if (lastError.code === 'ECONNABORTED' || lastError.message.includes('timeout')) {
      throw new Error(`Upload timed out. Large files may take up to 30 minutes to upload.`);
    } else if (lastError.response?.status >= 500) {
      throw new Error(`Server error occurred. Please try again later.`);
    } else {
      throw new Error(`Upload failed: ${lastError.message}`);
    }
  };

  // Helper function to format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helper function to format time duration
  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  return {
    addSoftwareDeployment,
    validateFile,
    formatFileSize,
    formatDuration,
    MAX_FILE_SIZE,
    LARGE_FILE_THRESHOLD,
  };
};
